import React, { useState, useEffect, useRef } from 'react';

interface KeyMetricsCardsProps {
  ftp_indoor: number | null;
  ftp_outdoor: number | null;
  weight_kg: number | null;
  w_per_kg: number | null;
  cp: number | null;
  w_prime: number | null;
  vo2max: number | null;
}

const formatValue = (val: number | null | undefined, decimals: number = 0): string => {
  if (val === null || val === undefined) return '--';
  return decimals > 0 ? val.toFixed(decimals) : Math.round(val).toString();
};

const InfoIcon: React.FC<{ info: string; isOpen: boolean; onToggle: () => void }> = ({ info, isOpen, onToggle }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onToggle();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onToggle]);

  useEffect(() => {
    if (isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      let left = rect.left + rect.width / 2 - 140;
      if (left < 8) left = 8;
      if (left + 280 > window.innerWidth - 8) left = window.innerWidth - 288;
      setPos({ top: rect.bottom + 4, left });
    }
  }, [isOpen]);

  return (
    <div ref={wrapperRef} className="analytics-info-wrapper">
      <button
        ref={btnRef}
        className="analytics-info-icon"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        aria-label="Show metric definition"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" stroke="#9ca3af" strokeWidth="1.2" />
          <text x="7" y="10.5" textAnchor="middle" fill="#9ca3af" fontSize="9" fontWeight="600" fontFamily="sans-serif">i</text>
        </svg>
      </button>
      {isOpen && pos && (
        <div className="analytics-info-popover" style={{ position: 'fixed', top: pos.top, left: pos.left }}>
          {info}
        </div>
      )}
    </div>
  );
};

const KeyMetricsCards: React.FC<KeyMetricsCardsProps> = ({
  ftp_indoor,
  ftp_outdoor,
  weight_kg,
  w_per_kg,
  cp,
  w_prime,
  vo2max,
}) => {
  const [openInfo, setOpenInfo] = useState<string | null>(null);

  const powerMetrics = [
    { label: 'FTP In', value: formatValue(ftp_indoor), unit: 'W', info: 'Functional Threshold Power (Indoor). Maximum power sustainable for ~1 hour on a trainer. Estimated from ramp test or 20-min test \u00d7 0.95.' },
    { label: 'FTP Out', value: formatValue(ftp_outdoor), unit: 'W', info: 'Functional Threshold Power (Outdoor). Maximum power sustainable for ~1 hour on the road. From your training goal or best outdoor effort.' },
    { label: 'CP', value: formatValue(cp), unit: 'W', info: 'Critical Power. The highest power you can sustain without accumulating fatigue over time. Derived from power-duration modeling.' },
    { label: "W'", value: w_prime !== null && w_prime !== undefined ? (w_prime / 1000).toFixed(1) : '--', unit: 'kJ', info: "W-prime (anaerobic work capacity). The fixed amount of work (in kJ) you can do above CP before exhaustion. Think of it as your 'matches' to burn." },
  ];

  const bodyMetrics = [
    { label: 'Weight', sublabel: 'avg 7d', value: formatValue(weight_kg, 1), unit: 'kg', info: 'Body weight averaged over the last 7 days from daily check-in entries. Falls back to latest single entry or profile weight if no recent data.' },
    { label: 'W/kg', value: formatValue(w_per_kg, 2), unit: '', info: 'Watts per kilogram. FTP divided by body weight. The key metric for climbing performance. Higher is better for hills.' },
    { label: 'VO\u2082max', value: formatValue(vo2max, 1), unit: '', info: 'Maximum oxygen uptake estimate. Calculated from your best power efforts and weight. Indicates aerobic fitness ceiling.' },
  ];

  const handleToggle = (label: string) => {
    setOpenInfo(prev => prev === label ? null : label);
  };

  const renderCard = (m: { label: string; sublabel?: string; value: string; unit: string; info: string }) => (
    <div key={m.label} className="analytics-metric-card">
      <InfoIcon
        info={m.info}
        isOpen={openInfo === m.label}
        onToggle={() => handleToggle(m.label)}
      />
      <div className="analytics-metric-label">
        {m.label}
        {m.sublabel && <span className="analytics-metric-sublabel">{m.sublabel}</span>}
      </div>
      <div className="analytics-metric-value">
        {m.value}
        {m.value !== '--' && <span className="analytics-metric-unit">{m.unit}</span>}
      </div>
    </div>
  );

  return (
    <div className="analytics-metrics-grouped">
      <div className="analytics-metrics-group">
        <div className="analytics-metrics-group-label">Power</div>
        <div className="analytics-metrics-row">
          {powerMetrics.map(renderCard)}
        </div>
      </div>
      <div className="analytics-metrics-group">
        <div className="analytics-metrics-group-label">Body</div>
        <div className="analytics-metrics-row">
          {bodyMetrics.map(renderCard)}
        </div>
      </div>
    </div>
  );
};

export default KeyMetricsCards;
